'use client';

import { useEffect, useRef, useState } from 'react';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { createClient } from '@/lib/local-api/client';

export interface ChatMedia {
  attachment_url?: string;
  attachment_type?: string;
  attachment_name?: string;
}

export function ChatMediaPreview({ message }: { message: ChatMedia }) {
  if (!message.attachment_url?.startsWith('/api/uploads?bucket=chat-media&path=')) return null;
  return message.attachment_type?.startsWith('video/') ? (
    <video src={message.attachment_url} controls preload="metadata" className="my-1 max-h-64 max-w-full rounded-lg" />
  ) : (
    <a href={message.attachment_url} target="_blank" rel="noopener noreferrer">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={message.attachment_url} alt={message.attachment_name || 'Chat attachment'} className="my-1 max-h-64 max-w-full rounded-lg" />
    </a>
  );
}

export default function ChatAttachment({ senderId, receiverId, onSent }: {
  senderId: string; receiverId: string; onSent: (message: any) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function send() {
    if (!file || busy) return;
    setBusy(true);
    try {
      const client = createClient();
      const path = `${senderId}/${receiverId}/${crypto.randomUUID()}`;
      const { error: uploadError } = await client.storage.from('chat-media').upload(path, file);
      if (uploadError) throw uploadError;
      const { data: url } = client.storage.from('chat-media').getPublicUrl(path);
      const { data, error } = await client.from('direct_messages').insert({
        sender_id: senderId, receiver_id: receiverId,
        content: file.type.startsWith('video/') ? 'Video' : 'Photo',
        attachment_url: url.publicUrl, attachment_type: file.type, attachment_name: file.name,
      }).select().single();
      if (error) throw error;
      onSent(data);
      setFile(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send attachment. Try again.');
    } finally { setBusy(false); }
  }

  return (
    <div className="relative shrink-0">
      <button type="button" aria-label="Attach photo or video" disabled={busy} onClick={() => input.current?.click()} className="p-2 text-primary disabled:opacity-50">
        <PaperClipIcon className="h-5 w-5" />
      </button>
      <input ref={input} type="file" className="hidden" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime" onChange={(event) => {
        const selected = event.target.files?.[0];
        event.target.value = '';
        if (!selected) return;
        if (selected.size > 15 * 1024 * 1024) { toast.error('Choose a file no larger than 15 MB.'); return; }
        setFile(selected);
      }} />
      {file && (
        <div className="absolute bottom-full left-0 z-50 mb-2 w-56 rounded-xl border border-border bg-card p-3 shadow-xl">
          {file.type.startsWith('video/') ? <video src={preview} controls className="h-28 w-full object-contain" /> : <img src={preview} alt="Attachment preview" className="h-28 w-full object-contain" />}
          <p className="my-2 truncate text-xs">{file.name}</p>
          <div className="flex justify-between gap-2 text-sm">
            <button type="button" disabled={busy} onClick={() => setFile(null)}>Cancel</button>
            <button type="button" disabled={busy} onClick={send} className="rounded-lg bg-primary px-3 py-1 text-primary-foreground">{busy ? 'Sending...' : 'Send media'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
